import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './RepositorySelector.css';

const RepositorySelector = ({ repositories, onRepositorySelect, autoSelectSingle = true }) => {
  const [selectedRepository, setSelectedRepository] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { updateAuthData } = useAuth();

  useEffect(() => {
    // Auto-select if only one repository and autoSelectSingle is true
    if (repositories && repositories.length === 1 && autoSelectSingle) {
      handleRepositorySelection(repositories[0].code);
    }
    // Auto-select default repository if available
    else if (repositories && repositories.length > 1) {
      const defaultRepo = repositories.find(repo => repo.isDefault);
      if (defaultRepo) {
        setSelectedRepository(defaultRepo.code);
      }
    }
  }, [repositories, autoSelectSingle]);

  const handleRepositorySelection = async (repositoryCode) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/select-tenant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ tenantCode: repositoryCode })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to select tenant');
      }

      // Update auth context with new token and tenant info
      localStorage.setItem('token', data.token);
      const selection = data.selectedTenant || data.selectedRepository;
      if (selection?.code) {
        localStorage.setItem('selectedTenantCode', selection.code);
      }
      updateAuthData({
        user: data.user,
        token: data.token,
        selectedTenant: selection,
        selectedRepository: selection
      });

      // Call callback if provided
      if (onRepositorySelect) {
        onRepositorySelect(selection);
      }

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Tenant selection error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedRepository) {
      handleRepositorySelection(selectedRepository);
    }
  };

  const getAccessLevelColor = (accessLevel) => {
    switch (accessLevel) {
      case 'Admin':
        return 'admin';
      case 'Write':
        return 'write';
      case 'Read':
        return 'read';
      default:
        return 'read';
    }
  };

  const getAccessLevelIcon = (accessLevel) => {
    switch (accessLevel) {
      case 'Admin':
        return '👑';
      case 'Write':
        return '✏️';
      case 'Read':
        return '👁️';
      default:
        return '👁️';
    }
  };

  if (!repositories || repositories.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              No Tenants Available
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You don't have access to any tenants. Please contact your administrator.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="btn-primary"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-2xl w-full">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Select Tenant
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Choose which tenant you'd like to access
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {repositories.map((repo) => (
              <div
                key={repo.code}
                className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                  selectedRepository === repo.code
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                onClick={() => setSelectedRepository(repo.code)}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                      getAccessLevelColor(repo.accessLevel) === 'admin' ? 'bg-purple-500' :
                      getAccessLevelColor(repo.accessLevel) === 'write' ? 'bg-green-500' :
                      'bg-blue-500'
                    }`}>
                      {repo.name.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                        {repo.name}
                      </h3>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {getAccessLevelIcon(repo.accessLevel)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {repo.description || 'No description available'}
                    </p>
                    <div className="flex items-center mt-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        getAccessLevelColor(repo.accessLevel) === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300' :
                        getAccessLevelColor(repo.accessLevel) === 'write' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' :
                        'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                      }`}>
                        {repo.accessLevel} Access
                      </span>
                      {repo.isDefault && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
                          Default
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center pt-6">
            <button
              type="submit"
              disabled={!selectedRepository || loading}
              className="btn-primary px-8 py-3 text-lg"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-3"></div>
                  Selecting...
                </div>
              ) : (
                `Access ${selectedRepository ? repositories.find(r => r.code === selectedRepository)?.name : 'Tenant'}`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RepositorySelector;